import type { AnalystNoteResponse } from "@credit-acceptance/step6-bff-client";

/**
 * A single analyst note, normalized for the UI.
 *
 * The generated `AnalystNoteResponse` types every field as optional (`?: string`)
 * because the BFF forwards recap-service payloads verbatim. The table renders
 * verbatim values, so we keep the string fields but coerce the absent case to an
 * empty string at the mapping boundary (see {@link toAnalystNote}) — that keeps
 * the render path free of `?? ""` noise.
 */
export interface AnalystNote {
  /** Raw date/time the note was recorded in recap (formatted for display at render). */
  createdDatetime: string;
  /** User id of the analyst who authored the note. */
  authorUserId: string;
  /** Free-form analyst note text. */
  note: string;
}

/**
 * Normalizes a raw `AnalystNoteResponse` into an {@link AnalystNote}, coercing
 * each absent field to `""` so the table renders a blank cell rather than
 * "undefined".
 * @param {AnalystNoteResponse} raw - A single analyst note from the BFF.
 * @returns {AnalystNote} The normalized note.
 */
export function toAnalystNote(raw: AnalystNoteResponse): AnalystNote {
  return {
    createdDatetime: raw.createdDatetime ?? "",
    authorUserId: raw.authorUserId ?? "",
    note: raw.note ?? "",
  };
}
